import { useState, useRef, useEffect } from "react";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function fmtSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

const STATUS_OPTIONS = ["Ordered", "Partially Received", "Received", "Closed / Invoiced"];
const STATUS_CLASSES = {
  "Ordered":             "pill-ordered",
  "Received":            "pill-received",
  "Partially Received":  "pill-partial",
  "Closed / Invoiced":   "pill-closed",
};

function StatusPill({ status, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const current = status || "Ordered";
  const cls = STATUS_CLASSES[current] || "pill-ordered";

  return (
    <span ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <span
        className={`pill ${cls}`}
        style={{ cursor: "pointer", userSelect: "none" }}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        title="Click to change status"
      >
        {current}
        <i className="ti ti-chevron-down" style={{ fontSize: 11, marginLeft: 4, verticalAlign: -1 }} aria-hidden="true" />
      </span>
      {open && (
        <div className="status-dropdown" onClick={(e) => e.stopPropagation()}>
          {STATUS_OPTIONS.map((s) => (
            <div
              key={s}
              className={`status-option${s === current ? " active" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                onChange && onChange(s);
                setOpen(false);
              }}
            >
              <span className={`pill ${STATUS_CLASSES[s]}`}>{s}</span>
            </div>
          ))}
        </div>
      )}
    </span>
  );
}

export default function POList({ pos, loading, selectedPO, onSelect, onMove, onStatusChange, searchQuery, isSearchResults }) {
  const filtered = searchQuery
    ? pos.filter((p) => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : pos;

  if (loading) {
    return (
      <div className="state-box">
        <div className="spinner" />
        <p>Loading purchase orders…</p>
      </div>
    );
  }
  if (!loading && pos.length === 0) {
    return (
      <div className="state-box">
        <i className={isSearchResults ? "ti ti-search-off" : "ti ti-folder-open"} aria-hidden="true" />
        <p>{isSearchResults ? "No matching documents" : "No PDFs in this folder"}</p>
      </div>
    );
  }
  if (filtered.length === 0) {
    return (
      <div className="state-box">
        <i className="ti ti-search" aria-hidden="true" />
        <p>No results for "{searchQuery}"</p>
      </div>
    );
  }

  return (
    <>
      <div className="list-header">
        <div>Document</div>
        <div>Size</div>
        <div>Modified</div>
        <div>Status</div>
        <div />
      </div>
      {filtered.map((po) => {
        const status = po.fields?.Status || "Ordered";
        return (
          <div
            key={po.id}
            className={`po-row${selectedPO?.id === po.id ? " selected" : ""}`}
            onClick={() => onSelect(po)}
          >
            <div className="po-filename">
              <i className="ti ti-file-type-pdf pdf-icon" aria-hidden="true" />
              <div style={{ minWidth: 0, overflow: "hidden" }}>
                <div title={po.name} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{po.name}</div>
                {isSearchResults && po.parentReference?.name && (
                  <div style={{ fontSize: "11px", color: "var(--text3)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <i className="ti ti-building-store" style={{ fontSize: 11, verticalAlign: -1, marginRight: 3 }} aria-hidden="true" />
                    {po.parentReference.name}
                  </div>
                )}
              </div>
            </div>
            <div className="po-size">{fmtSize(po.size)}</div>
            <div className="po-date">{fmtDate(po.lastModifiedDateTime)}</div>
            <div>
              <StatusPill
                status={status}
                onChange={(s) => onStatusChange && onStatusChange(po.id, s)}
              />
            </div>
            <div className="row-actions">
              <button
                className="icon-btn"
                title="Move to another vendor"
                onClick={(e) => { e.stopPropagation(); onMove(po); }}
                aria-label="Move document"
              >
                <i className="ti ti-folder-symlink" aria-hidden="true" />
              </button>
            </div>
          </div>
        );
      })}
    </>
  );
}
